import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions as fbFunctions } from '../config/firebase';
import { isPro, subscribeUserPlan, UserPlan } from '../utils/planGate';

interface UpgradeToProModalProps {
  isOpen: boolean;
  onClose: () => void;
  context?: 'design' | 'team';
}

const UpgradeToProModal: React.FC<UpgradeToProModalProps> = ({ isOpen, onClose, context = 'design' }) => {
  const navigate = useNavigate();
  const [plan, setPlan] = React.useState<UserPlan>('free');
  const [loading, setLoading] = React.useState(false);

  // Pull plan to toggle CTA label
  React.useEffect(() => {
    // We subscribe in pages that have auth, but duplicate here defensively
    const authStr = localStorage.getItem('firebase:authUser');
    // Not reliable; rely on existing subscribers mainly. Keep CTA visible.
    const uid = undefined;
    if (!uid) return;
    const unsub = subscribeUserPlan(uid, (p) => setPlan(p));
    return () => { if (unsub) unsub(); };
  }, []);

  if (!isOpen) return null;

  const title = context === 'team' ? 'Create more teams with Pro' : 'Create more designs with Pro';
  const subtitle = context === 'team'
    ? 'Free plan allows up to 3 teams. Upgrade to Pro for unlimited teams and more.'
    : 'Free plan allows up to 3 designs. Upgrade to Pro for unlimited boards and more.';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2 max-h-[90vh]">
        {/* Left: Details */}
        <div className="p-6 md:p-8 flex flex-col h-full overflow-y-auto">
          <div className="mb-4">
            <span className="inline-flex items-center text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full border border-amber-200">Upgrade to unlock</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Go Pro</h2>
          <div className="mt-1 text-gray-600 text-sm">{subtitle}</div>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-gray-900">CAD 12</span>
            <span className="text-gray-500">/month</span>
          </div>
          <ul className="mt-6 space-y-3 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-green-700 text-xs">✓</span>
              <span>Unlimited personal designs</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-green-700 text-xs">✓</span>
              <span>Premium templates & assets</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-green-700 text-xs">✓</span>
              <span>Advanced export (PNG/PDF)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-green-700 text-xs">✓</span>
              <span>10GB cloud storage</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-green-700 text-xs">✓</span>
              <span>Priority support</span>
            </li>
          </ul>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              disabled={loading}
              onClick={async () => {
                if (isPro(plan)) {
                  try {
                    setLoading(true);
                    const portal = httpsCallable(fbFunctions, 'createPortalLink');
                    const res: any = await portal({});
                    window.location.href = res.data.url;
                  } finally { setLoading(false); }
                  return;
                }
                try {
                  setLoading(true);
                  // Fallback to HTTP endpoint to avoid callable CORS/preflight issues in some hosts
                  const { getAuth } = await import('firebase/auth');
                  const currentUser = getAuth().currentUser;
                  if (!currentUser) throw new Error('Not authenticated');
                  const idToken = await currentUser.getIdToken(true);
                  // Use redirect endpoint to avoid CORS entirely
                  const redirectUrl = `https://us-central1-stylesync-4d5cb.cloudfunctions.net/createCheckoutSessionRedirect?token=${encodeURIComponent(idToken)}`;
                  window.location.href = redirectUrl;
                } catch (e) {
                  // final fallback to callable if HTTP fails
                  try {
                    const createSession = httpsCallable(fbFunctions, 'createCheckoutSession');
                    const res: any = await createSession({});
                    window.location.href = res.data.url;
                  } catch (err) {
                    console.error(err);
                  }
                } finally {
                  setLoading(false);
                }
              }}
              className="flex-1 px-4 py-3 rounded-lg text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow text-center"
            >
              {isPro(plan) ? (loading ? 'Opening…' : 'Manage billing') : (loading ? 'Redirecting…' : 'Upgrade to Pro')}
            </button>
            <button onClick={onClose} className="px-4 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Not now</button>
          </div>
          <div className="mt-3 text-xs text-gray-500">No trial • Cancel anytime • Secure checkout via Stripe</div>
        </div>

        {/* Right: Animated Preview */}
        <div className="hidden md:block bg-gray-50 p-6 overflow-y-auto">
          <div className="relative bg-white rounded-xl border shadow-sm overflow-hidden">
            {/* Header bar */}
            <motion.div
              className="h-10 bg-gradient-to-r from-blue-600 to-purple-600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            />
            {/* Board grid */}
            <div className="relative p-4 grid grid-cols-3 gap-2">
              {/* Images with subtle zoom animation */}
              <motion.img
                src="https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=600&auto=format&fit=crop"
                alt="Preview 1"
                className="h-24 w-full object-cover rounded"
                initial={{ scale: 1.05, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8 }}
              />
              <motion.img
                src="https://images.unsplash.com/photo-1501973801540-537f08ccae7b?q=80&w=600&auto=format&fit=crop"
                alt="Preview 2"
                className="h-24 w-full object-cover rounded"
                initial={{ scale: 1.05, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.05 }}
              />
              <motion.img
                src="https://images.unsplash.com/photo-1495567720989-cebdbdd97913?q=80&w=600&auto=format&fit=crop"
                alt="Preview 3"
                className="h-24 w-full object-cover rounded"
                initial={{ scale: 1.05, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.1 }}
              />
              <motion.img
                src="https://images.unsplash.com/photo-1491553895911-0055eca6402d?q=80&w=1000&auto=format&fit=crop"
                alt="Preview 4"
                className="h-24 w-full object-cover rounded col-span-2"
                initial={{ scale: 1.05, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.15 }}
              />
              <motion.img
                src="https://images.unsplash.com/photo-1504196606672-aef5c9cefc92?q=80&w=600&auto=format&fit=crop"
                alt="Preview 5"
                className="h-24 w-full object-cover rounded"
                initial={{ scale: 1.05, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              />
              <motion.img
                src="https://images.unsplash.com/photo-1520975916090-3105956dac38?q=80&w=600&auto=format&fit=crop"
                alt="Preview 6"
                className="h-24 w-full object-cover rounded"
                initial={{ scale: 1.05, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.25 }}
              />
              <motion.img
                src="https://images.unsplash.com/photo-1545239351-1141bd82e8a6?q=80&w=1000&auto=format&fit=crop"
                alt="Preview 7"
                className="h-24 w-full object-cover rounded col-span-2"
                initial={{ scale: 1.05, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />

              {/* Animated cursor path */}
              <motion.div
                className="pointer-events-none absolute top-4 left-4"
                style={{ width: 18, height: 26 }}
                animate={{
                  x: [16, 220, 420, 220, 16],
                  y: [16, 16, 16, 140, 140],
                }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              >
                <svg viewBox="0 0 24 24" width="18" height="26" className="drop-shadow">
                  <path d="M3 2l16 8-6 2 2 8-4-6-8 2z" fill="#fff" />
                  <path d="M3 2l16 8-6 2 2 8-4-6-8 2z" fill="#000" opacity=".15" />
                </svg>
              </motion.div>
              {/* Click ripple */}
              <motion.span
                className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-blue-400"
                style={{ width: 12, height: 12 }}
                animate={{
                  x: [24, 228, 428, 228, 24],
                  y: [24, 24, 24, 148, 148],
                  scale: [0.6, 1.4, 0.6, 1.4, 0.6],
                  opacity: [0, 1, 0, 1, 0],
                }}
                transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeToProModal;


